// Ana sayfa takvim widget'ı — Blazor karşılığı:
// Octo.Web.Server/Components/Widgets/CalendarWidget.razor (widget ızgarasındaki DxScheduler kartı).
// Küçük ay ızgarası + yaklaşan etkinlikler; bir güne tıklanınca o günün etkinlikleri listelenir.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/IconButton';
import { Spinner } from '@/shared/ui/Spinner';
import { useElementWidth } from '@/features/home/hooks/useElementWidth';
import { useCalendar } from './hooks/useCalendar';
import {
  addMonths,
  buildMonthGrid,
  dateKey,
  expandEventsByDay,
  formatEventTime,
  isSameDay,
  monthName,
  relativeDayKey,
  startOfDay,
  upcomingEvents,
  weekdayLabels,
} from './model/calendarModel';
import type { CalendarEvent } from './model/calendarTypes';
import { CalendarEventDialog, type CalendarEventDraft } from './ui/CalendarEventDialog';

export interface CalendarWidgetProps {
  /** Dar kartlar için yoğun yerleşim (başlık gizlenir, daha az satır). */
  compact?: boolean;
  className?: string;
}

export function CalendarWidget({ compact = false, className }: CalendarWidgetProps) {
  const { t, i18n } = useTranslation();
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selected, setSelected] = useState<CalendarEvent | null>(null);
  /** Yeni etkinlik taslağı: "+" düğmesi ya da güne çift tık ile açılır. */
  const [draft, setDraft] = useState<CalendarEventDraft | null>(null);
  const locale = i18n.language || 'tr-TR';
  /**
   * Geniş kartta ay ızgarası ile liste YAN YANA durur. Alt alta dizilişte varsayılan
   * boyutta (8 kolon) ızgara enine yayılıyor, hücreler yassılaşıyor ve listeye ancak
   * birkaç satır kalıyordu. Eşik: ızgaranın rahat okunduğu genişlik + listeye anlamlı
   * bir sütun.
   */
  const { width, ref: sizeRef } = useElementWidth();
  const wide = width >= 520;

  const calendar = useCalendar({ anchor, view: 'month' });
  const { events, visibleEvents, sources, isLoading } = calendar;

  const grid = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const days = useMemo(() => grid.weeks.flat().map((c) => c.date), [grid]);
  const byDay = useMemo(() => expandEventsByDay(visibleEvents, days), [visibleEvents, days]);
  const labels = useMemo(() => weekdayLabels(locale), [locale]);

  // Etkinlik yalnızca yazılabilir (yerel) bir kaynak varsa eklenebilir; salt okunur
  // aboneliklerde (iCal/Google) düğmeyi göstermek boşa umut verir.
  const canCreate = sources.some((x) => !x.isReadOnly && x.isEnabled);

  /** Seçili gün (yoksa bugün) için bir saatlik taslak. */
  const startDraft = (day: Date) => {
    const now = new Date();
    const base = new Date(day);
    base.setHours(isSameDay(day, now) ? now.getHours() + 1 : 9, 0, 0, 0);
    const end = new Date(base.getTime() + 60 * 60 * 1000);
    setDraft({ start: base, end, allDay: false });
  };

  const listLimit = compact ? 3 : 5;
  /** Yaklaşanlarda gün: bugün/yarın ad ile, diğerleri "21 Eylül" biçiminde. */
  const dayLabel = (d: Date) => {
    const rel = relativeDayKey(d);
    return rel ? t(`calendar.rel.${rel}`) : `${d.getDate()} ${monthName(d.getMonth(), locale)}`;
  };

  const list = useMemo(() => {
    if (selectedDay) {
      const key = dateKey(selectedDay);
      return (byDay.get(key) ?? []).slice(0, 20);
    }
    return upcomingEvents(events, new Date(), listLimit);
  }, [selectedDay, byDay, events, listLimit]);

  return (
    <div ref={sizeRef} className={cn('flex h-full min-h-0 flex-col gap-2', className)}>
      <div className="flex items-center gap-1">
        <span className="text-fg min-w-0 flex-1 truncate text-sm font-semibold">
          {monthName(grid.monthIndex, locale)} {grid.year}
        </span>
        {isLoading ? <Spinner className="text-fg-subtle h-3.5 w-3.5" /> : null}
        {canCreate ? (
          <IconButton size="sm" label={t('calendar.event.newTitle')} onClick={() => startDraft(selectedDay ?? new Date())}>
            <CalendarPlus className="h-3.5 w-3.5" />
          </IconButton>
        ) : null}
        <IconButton
          size="sm"
          label={t('calendar.previous')}
          onClick={() => {
            setSelectedDay(null);
            setAnchor((a) => addMonths(a, -1));
          }}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </IconButton>
        {/* "Bugün" metinle yazılıyor: burada bir nokta işareti vardı ve ne yaptığı
            ancak üstüne gelince anlaşılıyordu. */}
        <button
          type="button"
          onClick={() => {
            setSelectedDay(null);
            setAnchor(startOfDay(new Date()));
          }}
          className="hover:bg-bg-hover focus-visible:ring-ring text-fg-muted rounded px-1.5 py-0.5 text-[11px] font-medium focus-visible:ring-2 focus-visible:outline-none"
        >
          {t('calendar.today')}
        </button>
        <IconButton
          size="sm"
          label={t('calendar.next')}
          onClick={() => {
            setSelectedDay(null);
            setAnchor((a) => addMonths(a, 1));
          }}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </IconButton>
      </div>

      <div className={cn('flex min-h-0 flex-1 gap-3', wide ? 'flex-row' : 'flex-col')}>
      {/* Ay ızgarası. Hücreler KARE DEĞİL: kare hücre geniş kartta kocaman, dar kartta
          minicik oluyordu. Izgara kendi alanını altı eşit satıra bölüyor, böylece ay
          widget hangi boyutta olursa olsun tam oturuyor. Dar kartta yükseklik listeyle
          3:2 paylaşılır; geniş kartta ızgara sabit genişlikte sol sütundur. */}
      <div className={cn('flex min-h-0 flex-col', wide ? 'w-[300px] shrink-0' : 'flex-[3]')}>
        <div className="text-fg-subtle grid grid-cols-7 pb-1 text-center text-[10px] font-medium uppercase">
          {labels.map((l, i) => (
            <div key={l} className={cn(i >= 5 && 'text-fg-subtle/70')}>{l}</div>
          ))}
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6 gap-0.5">
          {grid.weeks.flat().map((cell, index) => {
            const dayEvents = byDay.get(cell.key) ?? [];
            const isSelected = selectedDay ? isSameDay(cell.date, selectedDay) : false;
            const weekend = index % 7 >= 5;
            return (
              <button
                key={cell.key}
                type="button"
                onClick={() => setSelectedDay(isSelected ? null : cell.date)}
                onDoubleClick={() => canCreate && startDraft(cell.date)}
                aria-pressed={isSelected}
                aria-label={cell.key}
                title={dayEvents.length ? t('calendar.dayEventCount', { count: dayEvents.length }) : undefined}
                className={cn(
                  'focus-visible:ring-ring flex min-h-[26px] flex-col items-center justify-center rounded-md text-[11px] transition-colors focus-visible:ring-2 focus-visible:outline-none',
                  cell.inMonth ? (weekend ? 'text-fg-muted' : 'text-fg') : 'text-fg-subtle/60',
                  isSelected ? 'ring-brand-500 bg-brand-500/10 font-semibold ring-1' : 'hover:bg-bg-hover',
                )}
              >
                {/* Bugün dolu kare yerine yuvarlak rozet: seçim halkasıyla çakışmıyor ve
                    ızgarada tek bir odak noktası oluyor. */}
                <span className={cn('flex h-5 w-5 items-center justify-center rounded-full leading-none', cell.isToday && 'bg-brand-600 font-semibold text-white')}>
                  {cell.date.getDate()}
                </span>
                <span className="mt-0.5 flex h-1.5 items-center justify-center gap-0.5">
                  {dayEvents.slice(0, 3).map((e, i) => (
                    <span
                      key={`${cell.key}-${e.id}-${i}`}
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: e.color }}
                      aria-hidden="true"
                    />
                  ))}
                  {dayEvents.length > 3 ? <span className="text-fg-subtle text-[9px] leading-none">+{dayEvents.length - 3}</span> : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Yaklaşan / seçili gün */}
      <div className={cn('border-border min-h-0 overflow-auto', wide ? 'flex-1 border-l pl-3' : 'flex-[2] border-t pt-1.5')}>
        <div className="text-fg-subtle mb-1 text-[11px] font-medium uppercase">
          {selectedDay
            ? `${selectedDay.getDate()} ${monthName(selectedDay.getMonth(), locale)}`
            : t('calendar.upcoming')}
        </div>
        {list.length === 0 ? (
          <div className="py-2">
            <p className="text-fg-subtle text-xs">{t('calendar.noEvents')}</p>
            {canCreate ? (
              <button
                type="button"
                onClick={() => startDraft(selectedDay ?? new Date())}
                className="text-brand-600 hover:underline focus-visible:ring-ring mt-1 rounded text-xs focus-visible:ring-2 focus-visible:outline-none"
              >
                {t('calendar.event.newTitle')}
              </button>
            ) : null}
          </div>
        ) : (
          <ul className="space-y-0.5">
            {list.map((e) => (
              <li key={e.id}>
                {/* Saat solda sabit genişlikte: liste alt alta okunurken başlıklar aynı
                    hizada başlıyor, göz tek sütunu takip ediyor. */}
                <button
                  type="button"
                  onClick={() => setSelected(e)}
                  className="hover:bg-bg-hover focus-visible:ring-ring flex w-full items-center gap-2 rounded px-1.5 py-1 text-left focus-visible:ring-2 focus-visible:outline-none"
                >
                  <span className="text-fg-muted w-11 shrink-0 text-right text-[11px] tabular-nums">
                    {formatEventTime(e, t('calendar.allDay'))}
                  </span>
                  <span className="h-6 w-0.5 shrink-0 rounded-full" style={{ backgroundColor: e.color }} aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="text-fg block truncate text-xs font-medium">{e.title}</span>
                    {selectedDay ? null : <span className="text-fg-muted block truncate text-[11px]">{dayLabel(e.start)}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      </div>

      <CalendarEventDialog
        open={!!selected || !!draft}
        onOpenChange={(o) => {
          if (!o) {
            setSelected(null);
            setDraft(null);
          }
        }}
        event={selected}
        draft={draft}
        sources={sources}
        userId={calendar.userId}
        onSave={calendar.saveEvent}
        onDelete={calendar.deleteEvent}
        saving={calendar.isSavingEvent}
      />
    </div>
  );
}
